/**
 * QuestionContent Component
 *
 * Handles the question content with form field and conditional controls
 */

import { FormFieldType, IFormField } from '@ViewModels';
import { ISelectBoxOption } from '@WebComponents/SelectBox';
import { css } from 'aphrodite';
import * as React from 'react';
import { FormFieldWithOptions } from '../../../FormFieldWithOptions';
import { styleSheet } from '../../styles';
import { ConditionalQuestionControlsProps, ConditionalVisibilityProps } from '../../types';
import { ConditionalQuestionControls } from '../ConditionalQuestionControls';
import { ConditionalVisibilityControls } from '../ConditionalVisibilityControls';

interface QuestionContentProps {
	field: IFormField<string>;
	questionIndex: number;
	onToggleOptional: (index: number) => void;
	onLabelChange: (e: React.ChangeEvent<HTMLInputElement>, index: number) => void;
	onRenderTriggerTitle: (selectedOption: ISelectBoxOption<FormFieldType>) => React.ReactNode;
	onFieldTypeChange: (selectedType: ISelectBoxOption<FormFieldType>, index: number) => void;
	onFieldUpdate: (field: IFormField<string>, index: number) => void;
	isEdit: boolean;
	onAfterItem?: (optionLabel: string) => React.ReactNode;
	conditionalQuestionProps?: ConditionalQuestionControlsProps;
	conditionalVisibilityProps?: ConditionalVisibilityProps;
	formFieldOptions?: {
		title: string;
		value: FormFieldType;
	}[];
}

export const QuestionContent: React.FC<QuestionContentProps> = React.memo(
	({
		field,
		questionIndex,
		onToggleOptional,
		onLabelChange,
		onRenderTriggerTitle,
		onFieldTypeChange,
		onFieldUpdate,
		isEdit,
		onAfterItem,
		conditionalQuestionProps,
		conditionalVisibilityProps,
		formFieldOptions,
	}) => {
		return (
			<div
				className={css(
					styleSheet.section,
					styleSheet.sectionFullWidth,
					styleSheet.sectionNoBorderTop,
					styleSheet.borderRightBottom
				)}
			>
				<FormFieldWithOptions
					field={field}
					questionIndex={questionIndex}
					value={field.label}
					onToggleCheckChanged={onToggleOptional}
					onChange={onLabelChange}
					onRenderTriggerTitle={onRenderTriggerTitle}
					handleFormFieldTypeChange={onFieldTypeChange}
					onUpdateField={onFieldUpdate}
					isEdit={isEdit}
					onAfterItem={onAfterItem}
					formFieldOptions={formFieldOptions}
				>
					{conditionalQuestionProps && <ConditionalQuestionControls {...conditionalQuestionProps} />}
					{conditionalVisibilityProps && <ConditionalVisibilityControls {...conditionalVisibilityProps} />}
				</FormFieldWithOptions>
			</div>
		);
	}
);

QuestionContent.displayName = 'QuestionContent';
